// Hero prototype v2: scene layout (cover), scroll progress, water ripples, wind in the foliage, pause control.
// Effects move pixels of the original photograph only within masks derived from it; nothing is warped into fake 3D.
(() => {
  const IMG_W = 1916, IMG_H = 821;
  const wrap = document.getElementById('stage');
  const stage = wrap?.querySelector('.stage');
  const scene = document.getElementById('scene');
  const base = scene?.querySelector('.base');
  const interiorImage = stage?.querySelector('.interior img');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  if (!wrap || !scene || !base) return;

  // --- Layout: size the scene like object-fit: cover, anchored on a focus point per breakpoint.
  const layout = () => {
    const w = stage.clientWidth, h = stage.clientHeight;
    const mobile = w <= 900;
    // The photograph covers the whole stage; the focus point keeps the pavilion in view on narrow screens.
    const regionTop = 0, regionH = h;
    const s = Math.max(w / IMG_W, regionH / IMG_H);
    const sw = IMG_W * s, sh = IMG_H * s;
    const fx = w / h < 0.75 ? 0.82 : w / h < 1.25 ? 0.76 : 0.6, fy = mobile ? 0.4 : 0.45;   // keep the pavilion in frame on tall screens
    const left = (w - sw) * fx, top = regionTop + (regionH - sh) * fy;
    Object.assign(scene.style, { width: `${sw}px`, height: `${sh}px`, left: `${left}px`, top: `${top}px` });
    geo = { w, h, sw, sh, left, top };
  };

  // --- Camera path: cross the lake toward the open bay, then continue forward inside.
  // Focus is in photograph coordinates. In particular, it avoids the pillar at the old 84.5% target.
  const DOOR = { x: 1570 / IMG_W, y: 390 / IMG_H };
  let target = 0, shown = 0, easing = 0, geo = null, lastFollow = 0, interiorReady = false;
  const VARS = ['--p', '--e', '--f', '--z', '--ex', '--int', '--bl', '--pt', '--t', '--s', '--in-s', '--tx', '--ty', '--door-x', '--door-y', '--reveal-w', '--reveal-h', '--tun', '--fogout'];
  const seg = (p, a, b) => { const q = Math.min(1, Math.max(0, (p - a) / (b - a))); return q * q * (3 - 2 * q); };
  const mix = (a, b, t) => a + (b - a) * t;
  const apply = (p) => {
    const st = wrap.style;
    if (reduce.matches) {
      VARS.forEach((v) => st.removeProperty(v));
      interiorFull = false;
      return;
    }
    // Shorter, continuous journey: the camera never stops at the door; it keeps moving while the room opens
    // around it (enter overlaps the end of the travel), so the cut reads as passing through the doorway.
    const e = seg(p, 0, 0.22), f = seg(p, 0.05, 0.24), z = seg(p, 0.03, 0.30);
    const t = seg(p, 0, 0.80), aim = seg(p, 0, 0.48);
    const enter = interiorReady ? seg(p, 0.52, 0.70) : 0;   // short, decisive opening: no long double exposure
    const i = interiorReady ? seg(p, 0.555, 0.60) : 0;   // the room appears under the peak of the light flash
    const tunnel = Math.sin(Math.PI * seg(p, 0.30, 0.74));   // edges close in on approach, open once inside
    const fogOut = seg(p, 0.10, 0.36);                        // the camera passes through the mist: it parts and clears
    // The exterior stays opaque underneath until the soft reveal covers every viewport corner.
    const ex = interiorReady ? seg(p, 0.60, 0.63) : 0;
    const bl = interiorReady ? 1 * Math.sin(Math.PI * seg(p, 0.50, 0.68)) : 0;   // warm light flash at the threshold hides the cut
    const pt = interiorReady ? seg(p, 0.76, 0.90) : 0;
    const inScale = 1 + 0.09 * seg(p, 0.50, 0.96);          // the forward motion continues inside the room
    let s = 1.02;
    let tx = 0, ty = 0;
    if (geo) {
      const { w, h, sw, sh, left, top } = geo;
      const startX = (w * 0.5 - left) / sw, startY = (h * 0.5 - top) / sh;
      const focusX = mix(startX, DOOR.x, aim), focusY = mix(startY, DOOR.y, aim);
      const coverAt = (x, y) => Math.max(w / (2 * x * sw), w / (2 * (1 - x) * sw), h / (2 * y * sh), h / (2 * (1 - y) * sh));
      const endScale = Math.max(w <= 900 ? 3.6 : 4.4, coverAt(DOOR.x, DOOR.y) * 1.06);
      // Logarithmic scale gives constant proportional advance; aim arrives ahead of it, so the
      // pavilion moves into the frame before the final approach. Coverage is derived, never guessed.
      s = Math.max(1.02 * Math.pow(endScale / 1.02, t), coverAt(focusX, focusY) * 1.01);
      const cameraX = w * 0.5 - focusX * sw * s, cameraY = h * 0.5 - focusY * sh * s;
      tx = cameraX - left; ty = cameraY - top;
      const doorX = cameraX + DOOR.x * sw * s, doorY = cameraY + DOOR.y * sh * s;
      const reveal = Math.pow(enter, 0.8);
      st.setProperty('--door-x', mix(doorX, w * 0.5, enter).toFixed(2) + 'px');
      st.setProperty('--door-y', mix(doorY, h * 0.5, enter).toFixed(2) + 'px');
      st.setProperty('--reveal-w', (w * mix(0.10, 1.65, reveal)).toFixed(2) + 'px');
      st.setProperty('--reveal-h', (h * mix(0.16, 1.85, reveal)).toFixed(2) + 'px');
    }
    interiorFull = ex > 0.999;   // exterior fully dissolved: its effects can stop
    const vals = { '--p': p, '--e': e, '--f': f, '--z': z, '--ex': ex, '--int': i, '--bl': bl, '--pt': pt, '--t': t, '--s': s, '--in-s': inScale, '--tun': tunnel, '--fogout': fogOut };
    for (const v of VARS) if (v in vals) st.setProperty(v, vals[v].toFixed(4));
    st.setProperty('--tx', tx.toFixed(1) + 'px'); st.setProperty('--ty', ty.toFixed(1) + 'px');
  };
  const measure = () => {
    const r = wrap.getBoundingClientRect();
    const span = Math.max(1, wrap.offsetHeight - stage.offsetHeight);
    target = Math.min(1, Math.max(0, -r.top / span));
  };
  const follow = (now) => {
    easing = 0;
    const dt = lastFollow ? Math.min(64, now - lastFollow) : 1000 / 60;
    lastFollow = now;
    const d = target - shown;
    // Time-based damping feels the same on 60/120 Hz displays and reverses without a separate tween.
    shown = Math.abs(d) < 0.0001 ? target : shown + d * (1 - Math.exp(-dt / 115));
    apply(shown);
    if (shown !== target) easing = requestAnimationFrame(follow);
    else lastFollow = 0;
    kick();
  };
  const progress = () => {
    measure();
    if (reduce.matches) { cancelAnimationFrame(easing); easing = 0; lastFollow = 0; shown = target; apply(shown); return; }
    if (!easing) { lastFollow = 0; easing = requestAnimationFrame(follow); }
  };

  // --- Image sources for the effects (same-origin, so canvas pixels can be read).
  const load = (src) => new Promise((ok, err) => { const i = new Image(); i.decoding = 'async'; i.onload = () => ok(i); i.onerror = err; i.src = src; });

  const waterCanvas = scene.querySelector('.fx-water');
  const W = { y0: 496, h: 325 };
  // Foliage regions in photograph pixels. A: cherry blossoms and the pine spray above the roof. B: the large pine by the shore.
  const REGIONS = [
    { name: 'canopy', x0: 1380, y0: 0, w: 536, h: 290, canvas: '.fx-wind', mask: window.ssrAsset('ssr-mask-foliage.png'),
      edges: { left: true, bottom: true },          // interior borders of this layer stay still
      classify: (R, G, B, L, x, y, soft, tex) => {
        if (tex < 0.035) return false;               // smooth sky or haze is never foliage
        const inPine = x >= 10 && x <= 190 && y >= 35 && y <= 165;
        return soft > 0.05 && (inPine ? (L < 0.6 && !(R > B + 15)) || (L > 0.5 && R > B + 4) : soft > 0.35);
      },
      kind: (P) => (P.cx < 200 && P.maxY < 200 ? 'pine' : 'blossom'),
      anchor: (P, kind) => kind === 'pine' ? [P.maxX, (P.minY + P.maxY) / 2] : [(P.minX + P.maxX) / 2, P.minY] },
    { name: 'shore-pine', x0: 985, y0: 272, w: 410, h: 205, canvas: '.fx-wind-b', mask: null,
      // Needles are dark and neutral against the bright mist; the warm-dark trunk and the bright haze are excluded.
      edges: { left: true, top: true, right: true, bottom: true },
      classify: (R, G, B, L, x, y, soft, tex) => {
        const X = 985 + x, Y = 272 + y;              // photograph coordinates
        if (X > 1382) return false;                  // pavilion posts and glass
        if (Y < 306 && X > 1222) return false;       // roof eave
        if (Y > 418 && X > 1335) return false;       // lantern and platform
        return tex > 0.03 && L < 0.44 && L > 0.04 && !(R > B + 18) && y < 165;
      },
      kind: () => 'pine',
      anchor: (P) => { const tx = 250, ty = 150; return [Math.min(P.maxX, Math.max(P.minX, tx)), Math.min(P.maxY, Math.max(P.minY, ty))]; } },
  ];
  let water = null, winds = [];

  // Fog texture: render the tileable SVG noise once into a bitmap with its vertical fade baked in, so the
  // drifting layers are plain image layers (no CSS mask, no blend mode, no re-rasterising while zooming).
  async function buildFog() {
    const svg = "<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='360'><filter id='f' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='0.0035 0.011' numOctaves='4' seed='7' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 0.98  0 0 0 0 0.95  0 0 0 2.1 -0.82'/></filter><rect width='100%' height='100%' filter='url(%23f)'/></svg>";
    const img = await load('data:image/svg+xml;utf8,' + svg.replace(/#/g, '%23'));
    const c = document.createElement('canvas'); c.width = 1200; c.height = 360;
    const x = c.getContext('2d'); x.drawImage(img, 0, 0);
    const g = x.createLinearGradient(0, 0, 0, 360);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(0.3, '#000'); g.addColorStop(0.66, '#000'); g.addColorStop(1, 'rgba(0,0,0,0)');
    x.globalCompositeOperation = 'destination-in'; x.fillStyle = g; x.fillRect(0, 0, 1200, 360);
    const blob = await new Promise((ok) => c.toBlob(ok, 'image/png'));
    scene.style.setProperty('--fog-bmp', `url(${URL.createObjectURL(blob)})`);
  }

  // Water (WebGL): a smooth two-dimensional ripple field over the full-resolution photograph, so the reflections
  // break up and drift like a lake surface instead of whole rows sliding. The canvas renders at screen resolution.
  // Falls back to the row-based 2D version below when WebGL is not available.
  const WATER_FS = `precision highp float;
uniform sampler2D img; uniform sampler2D msk; uniform float t; varying vec2 uv;
void main() {
  float d = uv.y;                        // 0 at the horizon, 1 at the bottom edge
  float persp = pow(d, 1.35);
  float z = 60.0 * sqrt(d);              // perspective-compressed distance: swells lengthen towards the viewer
  float x = uv.x * 1916.0;
  float amp = 0.35 + 4.6 * persp;
  float dx = amp * (0.62 * sin(z * 1.9 - t * 1.6 + x * 0.004)
                  + 0.28 * sin(z * 3.7 - t * 2.3 + 1.3 - x * 0.007)
                  + 0.18 * sin(d * 14.6 + t * 0.45 + 0.7 + x * 0.002));
  float dy = (0.5 + 1.8 * persp) * sin(z * 2.6 - t * 1.9 + 0.4 + x * 0.006)
           + 0.45 * persp * sin(x * 0.021 - t * 1.1 + z * 0.8);
  vec2 q = clamp(uv + vec2(dx / 1916.0, dy / 325.0), 0.0, 1.0);
  vec4 c = texture2D(img, q);
  c.rgb *= 1.0 + persp * 0.045 * sin(z * 3.1 - t * 2.0 + x * 0.011);   // light catching the ripple crests
  float a = texture2D(msk, uv).a;
  gl_FragColor = vec4(c.rgb * a, a);
}`;
  function initGLWater(raw, mask) {
    const gl = waterCanvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
    if (!gl) return null;
    const k = (raw.naturalWidth || IMG_W) / IMG_W, tw = Math.round(IMG_W * k), th = Math.round(W.h * k);
    const tc = document.createElement('canvas'); tc.width = tw; tc.height = th;
    tc.getContext('2d').drawImage(raw, 0, W.y0 * k, tw, th, 0, 0, tw, th);
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, 'attribute vec2 p; varying vec2 uv; void main(){ uv = vec2(p.x * 0.5 + 0.5, 0.5 - p.y * 0.5); gl_Position = vec4(p, 0.0, 1.0); }'));
    gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, WATER_FS));
    gl.linkProgram(pr); if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error('link');
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const tex = (unit, source) => { const tx = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, tx);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source); };
    tex(0, tc); tex(1, mask);
    gl.uniform1i(gl.getUniformLocation(pr, 'img'), 0); gl.uniform1i(gl.getUniformLocation(pr, 'msk'), 1);
    const uT = gl.getUniformLocation(pr, 't');
    const size = () => { const w = Math.round(Math.min(tw, Math.max(320, waterCanvas.offsetWidth * Math.min(2, devicePixelRatio || 1)))); const hh = Math.round(w * W.h / IMG_W);
      if (waterCanvas.width !== w || waterCanvas.height !== hh) { waterCanvas.width = w; waterCanvas.height = hh; gl.viewport(0, 0, w, hh); } };
    size(); addEventListener('resize', size, { passive: true });
    return { gl, draw: (t) => { size(); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.uniform1f(uT, t); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4); } };
  }

  async function setup() {
    buildFog().catch(() => {});
    const [raw, wMask] = await Promise.all([load(base.currentSrc || base.src), load(window.ssrAsset('ssr-mask-water.png'))]);
    // Effects work in photograph coordinates (1916 × 821); a higher-resolution photograph is normalised for them.
    let photo = raw;
    if (raw.naturalWidth !== IMG_W) { photo = document.createElement('canvas'); photo.width = IMG_W; photo.height = IMG_H; photo.getContext('2d').drawImage(raw, 0, 0, IMG_W, IMG_H); }
    let glw = null; try { glw = initGLWater(raw, wMask); } catch (e) { glw = null; }
    if (glw) water = glw;
    else {
      // Water (2D fallback): source rows and a mask canvas.
      const ws = document.createElement('canvas'); ws.width = IMG_W; ws.height = W.h;
      ws.getContext('2d').drawImage(photo, 0, W.y0, IMG_W, W.h, 0, 0, IMG_W, W.h);
      water = { src: ws, mask: wMask, ctx: waterCanvas.getContext('2d') };
    }
    // Wind: each region is segmented into rigid pieces over a background plate.
    for (const R of REGIONS) {
      const c = document.createElement('canvas'); c.width = R.w; c.height = R.h;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(photo, R.x0, R.y0, R.w, R.h, 0, 0, R.w, R.h);
      const src = cx.getImageData(0, 0, R.w, R.h).data;
      const soft = new Float32Array(R.w * R.h).fill(1);
      if (R.mask) { const mk = await load(R.mask); cx.clearRect(0, 0, R.w, R.h); cx.drawImage(mk, 0, 0); const md = cx.getImageData(0, 0, R.w, R.h).data; for (let i = 0; i < soft.length; i++) soft[i] = md[i * 4 + 3] / 255; }
      winds.push(buildFoliage(src, soft, R, scene.querySelector(R.canvas).getContext('2d')));
    }
  }

  // Water: horizontal ripple per row, stronger towards the viewer, plus a slight vertical break-up of reflections.
  function drawWater(t) {
    if (water.draw) { water.draw(t); return; }
    const { ctx, src, mask } = water; const h = W.h;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, IMG_W, h);
    for (let y = 0; y < h; y += 2) {        // 2-px strips: half the draw calls, no visible difference
      const d = y / h;                         // 0 at the horizon, 1 at the bottom edge
      const persp = Math.pow(d, 1.35);
      // Wave phase uses a perspective-compressed distance, so swells travel towards the viewer and lengthen as they approach.
      const z = 60 * Math.sqrt(d);
      const amp = 0.35 + 4.6 * persp;
      const dx = amp * (0.62 * Math.sin(z * 1.9 - t * 1.6)
                      + 0.28 * Math.sin(z * 3.7 - t * 2.3 + 1.3)
                      + 0.18 * Math.sin(y * 0.045 + t * 0.45 + 0.7));
      // Smooth (sub-pixel) vertical break-up of reflections; rounding here is what made the motion step.
      let sy = y + (0.5 + 1.8 * persp) * Math.sin(z * 2.6 - t * 1.9 + 0.4);
      sy = sy < 0 ? 0 : sy > h - 2 ? h - 2 : sy;
      ctx.drawImage(src, 0, sy, IMG_W, 2, dx, y, IMG_W, 2);
    }
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
  }

  // Wind: smooth sway field with gusts; sample displaced pixels where the foliage mask allows it.
  // --- Foliage: split into rigid pieces (blossom clusters, pine sprays) that rotate about their attachment
  // point, over a background plate in which the original foliage pixels are filled from their surroundings.
  function buildFoliage(src, soft, R, ctx) {
    const w = R.w, h = R.h, n = w * h;
    // Refine: inside the pine zone keep only needle-coloured pixels (no sky), elsewhere keep the blossom mask.
    // Local texture: standard deviation of luminance in a 5x5 window (box filters on L and L²).
    const Lum = new Float32Array(n), L2 = new Float32Array(n);
    for (let i = 0; i < n; i++) { const p = i * 4; const v = (0.299 * src[p] + 0.587 * src[p + 1] + 0.114 * src[p + 2]) / 255; Lum[i] = v; L2[i] = v * v; }
    const box = (a) => { const t = new Float32Array(n), o = new Float32Array(n), r = 2, k = 2 * r + 1;
      for (let y = 0; y < h; y++) { let acc = 0; for (let x = -r; x <= r; x++) acc += a[y * w + Math.min(w - 1, Math.max(0, x))]; for (let x = 0; x < w; x++) { t[y * w + x] = acc / k; acc += a[y * w + Math.min(w - 1, x + r + 1)] - a[y * w + Math.max(0, x - r)]; } }
      for (let x = 0; x < w; x++) { let acc = 0; for (let y = -r; y <= r; y++) acc += t[Math.min(h - 1, Math.max(0, y)) * w + x]; for (let y = 0; y < h; y++) { o[y * w + x] = acc / k; acc += t[Math.min(h - 1, y + r + 1) * w + x] - t[Math.max(0, y - r) * w + x]; } }
      return o; };
    const mL = box(Lum), mL2 = box(L2);
    const E = R.edges || {}, M = 5;
    const hard = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const x = i % w, y = (i / w) | 0, p = i * 4, Rr = src[p], G = src[p + 1], B = src[p + 2];
      const L = (0.299 * Rr + 0.587 * G + 0.114 * B) / 255;
      const tex = Math.sqrt(Math.max(0, mL2[i] - mL[i] * mL[i]));
      const nearSeam = (E.left && x < M) || (E.right && x >= w - M) || (E.top && y < M) || (E.bottom && y >= h - M);
      hard[i] = !nearSeam && R.classify(Rr, G, B, L, x, y, soft[i], tex) ? 1 : 0;
    }
    // Close tiny gaps so petals of one cluster stay together (3x3 dilation).
    const grow = new Uint8Array(n);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let v = 0; for (let j = -1; j <= 1 && !v; j++) for (let k = -1; k <= 1; k++) { const X = x + k, Y = y + j; if (X >= 0 && Y >= 0 && X < w && Y < h && hard[Y * w + X]) { v = 1; break; } }
      grow[y * w + x] = v;
    }
    // Connected components (4-connectivity) -> pieces.
    const label = new Int32Array(n).fill(-1), pieces = [], stack = [];
    for (let i = 0; i < n; i++) {
      if (!grow[i] || label[i] >= 0) continue;
      const id = pieces.length; let minX = w, minY = h, maxX = 0, maxY = 0, cnt = 0, sx = 0;
      label[i] = id; stack.push(i);
      while (stack.length) {
        const q = stack.pop(), x = q % w, y = (q / w) | 0; cnt++; sx += x;
        if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
        const nb = [x > 0 ? q - 1 : -1, x < w - 1 ? q + 1 : -1, y > 0 ? q - w : -1, y < h - 1 ? q + w : -1];
        for (const r of nb) if (r >= 0 && grow[r] && label[r] < 0) { label[r] = id; stack.push(r); }
      }
      pieces.push({ minX, minY, maxX, maxY, cnt, cx: sx / cnt });
    }
    // Group neighbouring components into one rigid piece per 48-px cell (bounded draw calls per frame).
    {
      const CELL = 48, cols = Math.ceil(w / CELL), map = new Int32Array(pieces.length), groups = new Map();
      pieces.forEach((P, id) => { const key = ((P.minY + P.maxY) / 2 / CELL | 0) * cols + (P.cx / CELL | 0); if (!groups.has(key)) groups.set(key, groups.size); map[id] = groups.get(key); });
      const merged = Array.from({ length: groups.size }, () => ({ minX: w, minY: h, maxX: 0, maxY: 0, cnt: 0, sx: 0 }));
      pieces.forEach((P, id) => { const G = merged[map[id]]; G.minX = Math.min(G.minX, P.minX); G.minY = Math.min(G.minY, P.minY); G.maxX = Math.max(G.maxX, P.maxX); G.maxY = Math.max(G.maxY, P.maxY); G.cnt += P.cnt; G.sx += P.cx * P.cnt; });
      for (let i = 0; i < n; i++) if (label[i] >= 0) label[i] = map[label[i]];
      pieces.length = 0; merged.forEach((G) => pieces.push({ minX: G.minX, minY: G.minY, maxX: G.maxX, maxY: G.maxY, cnt: G.cnt, cx: G.sx / G.cnt }));
    }
    // Background plate: fill foliage pixels (dilated by 2) from the nearest surrounding pixels, ring by ring.
    const plate = new Uint8ClampedArray(src), known = new Uint8Array(n);
    const hole = new Uint8Array(n);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let v = 0; for (let j = -2; j <= 2 && !v; j++) for (let k = -2; k <= 2; k++) { const X = x + k, Y = y + j; if (X >= 0 && Y >= 0 && X < w && Y < h && grow[Y * w + X]) { v = 1; break; } } hole[y * w + x] = v; }
    for (let i = 0; i < n; i++) known[i] = hole[i] ? 0 : 1;
    let pending = true, guard = 0;
    while (pending && guard++ < 400) {
      pending = false; const fill = [];
      for (let i = 0; i < n; i++) {
        if (known[i]) continue;
        const x = i % w, y = (i / w) | 0; let r = 0, g = 0, b = 0, c = 0;
        for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) { const X = x + k, Y = y + j; if (X < 0 || Y < 0 || X >= w || Y >= h) continue; const q = Y * w + X; if (known[q]) { const p = q * 4; r += plate[p]; g += plate[p + 1]; b += plate[p + 2]; c++; } }
        if (c) fill.push(i, r / c, g / c, b / c); else pending = true;
      }
      for (let f = 0; f < fill.length; f += 4) { const p = fill[f] * 4; plate[p] = fill[f + 1]; plate[p + 1] = fill[f + 2]; plate[p + 2] = fill[f + 3]; known[fill[f]] = 1; }
      if (fill.length) pending = pending || fill.length > 0 && known.includes(0);
    }
    // Plate canvas: only the hole area is opaque (feathered), so it hides the original foliage and nothing else.
    const plateC = document.createElement('canvas'); plateC.width = w; plateC.height = h;
    const pctx = plateC.getContext('2d'); const pimg = pctx.createImageData(w, h);
    for (let i = 0; i < n; i++) { const p = i * 4; pimg.data[p] = plate[p]; pimg.data[p + 1] = plate[p + 1]; pimg.data[p + 2] = plate[p + 2]; pimg.data[p + 3] = hole[i] ? 255 : 0; }
    pctx.putImageData(pimg, 0, 0);
    // Sprites: each piece's own pixels with the soft mask as alpha.
    const sprites = [];
    for (let id = 0; id < pieces.length; id++) {
      const P = pieces[id], pw = P.maxX - P.minX + 3, ph = P.maxY - P.minY + 3;
      const c = document.createElement('canvas'); c.width = pw; c.height = ph;
      const cc = c.getContext('2d'); const img = cc.createImageData(pw, ph);
      for (let y = P.minY - 1; y <= P.maxY + 1; y++) for (let x = P.minX - 1; x <= P.maxX + 1; x++) {
        if (x < 0 || y < 0 || x >= w || y >= h) continue; const i = y * w + x; if (label[i] !== id) continue;
        const o = ((y - P.minY + 1) * pw + (x - P.minX + 1)) * 4, p = i * 4;
        img.data[o] = src[p]; img.data[o + 1] = src[p + 1]; img.data[o + 2] = src[p + 2];
        img.data[o + 3] = 255 * Math.min(1, Math.max(hard[i] ? 0.85 : 0.35, soft[i]));
      }
      cc.putImageData(img, 0, 0);
      const kind = R.kind(P), isPine = kind === 'pine';
      // Attachment point: blossoms hang from the branch above them; pine sprays grow from the trunk.
      const [ax, ay] = R.anchor(P, kind);
      const size = Math.sqrt(P.cnt);
      sprites.push({ c, x: P.minX - 1, y: P.minY - 1, ax, ay, isPine,
        // Degrees: smaller pieces flutter more; large sprays move as heavy branches.
        amp: (isPine ? 2.2 : 3.6) * Math.min(1.8, 14 / Math.max(6, size)) * Math.PI / 180,
        ph: ((R.x0 + P.cx) / IMG_W) * 4.5 + (id % 7) * 0.9, fr: 1.9 + (id % 5) * 0.41 });
    }
    return { ctx, plate: plateC, sprites, w, h, sway: R.name === 'canopy' ? 3.2 : 2.2 };
  }

  function drawWind(t, wind) {
    const { ctx, plate, sprites, w, h } = wind;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(plate, 0, 0);
    // A gust travels across the canopy; each piece adds its own faster flutter.
    const gust = 0.6 + 0.3 * Math.sin(t * 0.33) + 0.15 * Math.sin(t * 0.91 + 0.8);
    const sway = wind.sway * gust * Math.sin(t * 0.8);            // whole-branch drift in pixels
    for (const s of sprites) {
      const theta = s.amp * (gust * Math.sin(t * 1.15 - s.ph) + 0.45 * Math.sin(t * s.fr + s.ph * 1.7));
      const tx = (s.isPine ? 0.6 : 1) * sway * (0.6 + 0.4 * Math.sin(t * 0.5 + s.ph));
      ctx.setTransform(1, 0, 0, 1, s.ax + tx, s.ay + tx * 0.25);
      ctx.rotate(theta);
      ctx.drawImage(s.c, s.x - s.ax, s.y - s.ay);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  // --- Animation loop: only while the stage is visible, the tab is shown and motion is allowed.
  let visible = true, paused = false, raf = 0, lastWind = 0, interiorFull = false;
  // The exterior effects stop while the interior fully covers them.
  const running = () => visible && !paused && !interiorFull && !reduce.matches && !document.hidden && water && winds.length;
  const frame = (now) => {
    raf = 0;
    if (!running()) return;
    const t = now / 1000;
    drawWater(t);
    for (const wd of winds) drawWind(t, wd);
    raf = requestAnimationFrame(frame);
  };
  const kick = () => { if (!raf && running()) raf = requestAnimationFrame(frame); };

  const toggle = document.querySelector('.motion-toggle');
  toggle?.addEventListener('click', () => {
    paused = !paused;
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? 'Play motion' : 'Pause motion');
    stage.classList.toggle('is-paused', paused);
    kick();
  });

  new IntersectionObserver(([e]) => { visible = e.isIntersecting; kick(); }).observe(stage);
  document.addEventListener('visibilitychange', kick);
  addEventListener('scroll', progress, { passive: true });
  addEventListener('resize', () => { layout(); progress(); }, { passive: true });
  reduce.addEventListener?.('change', () => { progress(); kick(); });

  // QA hook (prototype only): render one effects frame at time t without the animation loop.
  window.__heroFx = { snap: () => { measure(); shown = target; apply(shown); kick(); return shown; }, render: (t = 1, only) => { if (!(water && winds.length)) return false; if (only !== 'wind') drawWater(t); if (only !== 'water') for (const wd of winds) drawWind(t, wd); return true; } };

  layout(); measure(); shown = target; apply(shown);
  interiorImage?.decode().then(() => { interiorReady = true; progress(); }).catch(() => { /* keep the exterior if the interior cannot load */ });
  // Effects start only once the visible photograph is decoded, so a layer can never appear without its background.
  // Loading gate: the stage stays covered in ivory until the photograph is decoded and the effect layers are built,
  // then the whole scene fades in at once, so no layer is ever seen without its background.
  let revealed = false;
  const reveal = () => { if (revealed) return; revealed = true; requestAnimationFrame(() => requestAnimationFrame(() => stage.classList.add('is-ready'))); };
  setTimeout(reveal, 6000);   // never leave the hero covered if something stalls
  // The photograph alone lifts the cover; the canvas effects join it (fading in) once they are built.
  // The heavy effect preparation (pixel analysis) starts only after the photograph is on screen, so it can never
  // hold back the first paint of the hero.
  const decoded = base.decode().catch(() => {});
  decoded.then(reveal);
  decoded.then(() => new Promise((ok) => setTimeout(ok, 150))).then(setup).then(() => { if (toggle && !reduce.matches) toggle.hidden = false; kick(); reveal(); requestAnimationFrame(() => scene.classList.add('fx-ready')); }).catch(() => { reveal(); /* effects are optional; the photograph stays */ });
})();
